import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  signIn,
  signOut,
  currentSession,
  completeNewPassword as completeNewPasswordChallenge,
  adoptTokens,
} from "./CognitoAuth.js";
import { ssoLogoutUrl } from "./Sso.js";

// Exported so a session can be provided directly — used by render harnesses
// and tests, which have no Cognito to authenticate against. Application code
// should go through useAuth() below rather than consuming this directly.
export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(undefined); // undefined = loading, null = signed out
  const [error, setError] = useState(null);
  // Set while a user is mid first-sign-in "set a new password" challenge.
  // { cognitoUser, userAttributes } | null
  const [newPasswordChallenge, setNewPasswordChallenge] = useState(null);

  useEffect(() => {
    currentSession()
      .then(setSession)
      .catch(() => setSession(null));
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const result = await signIn(email, password);
      if (result.status === "NEW_PASSWORD_REQUIRED") {
        setNewPasswordChallenge({ cognitoUser: result.cognitoUser, userAttributes: result.userAttributes });
        return "NEW_PASSWORD_REQUIRED";
      }
      setSession(result.session);
      return true;
    } catch (err) {
      setError(err.message || "Sign-in failed");
      return false;
    }
  }, []);

  const completeNewPassword = useCallback(
    async (newPassword) => {
      if (!newPasswordChallenge) return false;
      setError(null);
      try {
        const s = await completeNewPasswordChallenge(
          newPasswordChallenge.cognitoUser,
          newPassword,
          newPasswordChallenge.userAttributes
        );
        setSession(s);
        setNewPasswordChallenge(null);
        return true;
      } catch (err) {
        setError(err.message || "Could not set new password");
        return false;
      }
    },
    [newPasswordChallenge]
  );

  const cancelNewPassword = useCallback(() => {
    setNewPasswordChallenge(null);
    setError(null);
  }, []);

  // Called by the /auth/callback page with the tokens from a Google/Microsoft
  // sign-in.
  const completeSso = useCallback((tokens) => {
    setError(null);
    setSession(adoptTokens(tokens));
  }, []);

  const idToken = session ? session.getIdToken().getJwtToken() : null;
  const claims = session ? session.getIdToken().decodePayload() : null;

  const logout = useCallback(() => {
    // An SSO sign-in also leaves a session cookie on the Cognito domain, which
    // would sign the next "Continue with Google" straight back in as the same
    // person. Ending it there too makes Sign out mean what it says.
    const federated = Array.isArray(claims?.identities) && claims.identities.length > 0;
    signOut();
    setSession(null);
    const url = federated && ssoLogoutUrl();
    if (url) window.location.assign(url);
  }, [claims]);

  return (
    <AuthContext.Provider
      value={{
        session,
        idToken,
        claims,
        loading: session === undefined,
        error,
        login,
        logout,
        completeSso,
        newPasswordChallenge,
        completeNewPassword,
        cancelNewPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
